import { ref, onMounted } from 'vue'

interface StepData {
	title: string
	template: string
	src: string
}

/**
 * Custom hook for creating tooltips.
 * @param data - An array of StepData objects.
 * @returns An object with a containerRef property.
 */
export const useTooltip = (data: StepData[]) => {
	const containerRef = ref<HTMLElement | null>(null)

	const createTooltipElement = (
		content: string,
		header?: string,
	): HTMLElement => {
		const tooltipWrapper = document.createElement('div')
		tooltipWrapper.className = 'tooltip-wrapper tooltip-left' // 預設為 left

		// 新增包裹 header 和 content 的 div
		const tooltipContainer = document.createElement('div')
		tooltipContainer.className = 'tooltip-main'

		if (header) {
			const tooltipHeader = document.createElement('div')
			tooltipHeader.className = 'tooltip-header typography-s2-emp'
			tooltipHeader.textContent = header
			tooltipContainer.appendChild(tooltipHeader)
		}

		const tooltipContent = document.createElement('div')
		tooltipContent.className = 'tooltip-content typography-b3-reg'
		tooltipContent.textContent = content
		tooltipContainer.appendChild(tooltipContent)

		const tooltipArrow = document.createElement('div')
		tooltipArrow.className = 'tooltip-arrow'

		tooltipWrapper.appendChild(tooltipContainer)
		tooltipWrapper.appendChild(tooltipArrow)

		return tooltipWrapper
	}

	const adjustTooltipPosition = (
		element: HTMLElement,
		tooltip: HTMLElement,
	) => {
		const rect = element.getBoundingClientRect()
		const positionConditions: { [key: string]: boolean } = {
			'tooltip-left': rect.right + tooltip.clientWidth <= window.innerWidth,
			'tooltip-right': rect.left - tooltip.clientWidth >= 0,
		}

		tooltip.classList.remove('tooltip-left', 'tooltip-right')
		const newPosition = Object.keys(positionConditions).find(
			(position: keyof typeof positionConditions) =>
				positionConditions[position],
		)
		if (newPosition) {
			tooltip.classList.add(newPosition)
		}
	}

	const setupTooltip = (element: HTMLElement, tooltip: HTMLElement) => {
		element.addEventListener('click', () => {
			const isCurrentlyDisplayed = tooltip.style.display === 'block'
			tooltip.style.display = isCurrentlyDisplayed ? 'none' : 'block'

			if (!isCurrentlyDisplayed) {
				adjustTooltipPosition(element, tooltip)
			}
		})

		document.addEventListener(
			'click',
			event => {
				const target = event.target as HTMLElement
				if (
					tooltip !== target &&
					!tooltip.contains(target) &&
					element !== target
				) {
					tooltip.style.display = 'none'
				}

				if (target.tagName === 'A') {
					const { href } = target as HTMLAnchorElement
					window.open(href, '_self')
				}
			},
			true,
		)
	}

	const createTooltip = () => {
		try {
			data.forEach((step: StepData, index: number) => {
				const parser = new DOMParser()
				const doc = parser.parseFromString(step.template, 'text/html')

				const setupTooltipsInDocument = (
					documentFragment: DocumentFragment | HTMLElement,
				) => {
					const tooltipElements =
						documentFragment.querySelectorAll('[data-tooltip]')

					tooltipElements.forEach((el: Element) => {
						const header = el.getAttribute('data-header') || ''
						const content = el.getAttribute('data-tooltip') || ''
						const element = el as HTMLElement
						element.style.position = 'relative'

						const tooltip = createTooltipElement(content, header)
						element.appendChild(tooltip)
						setupTooltip(element, tooltip)
					})
				}

				setupTooltipsInDocument(doc.body)

				const stepContainers = containerRef.value?.querySelectorAll(
					`.step-${index}`,
				)
				stepContainers?.forEach(stepContainer => {
					stepContainer.innerHTML = ''
					// deep clone
					Array.from(doc.body.childNodes).forEach(childNode => {
						const clonedNode = childNode.cloneNode(true)
						stepContainer.appendChild(clonedNode)
						if (clonedNode.nodeType === Node.ELEMENT_NODE) {
							setupTooltipsInDocument(clonedNode as HTMLElement)
						}
					})
				})
			})
		} catch (error) {
			console.error('An error occurred while creating tooltips:', error)
		}
	}

	onMounted(() => {
		createTooltip()
	})

	return {
		containerRef,
	}
}
